import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { tv, type VariantProps } from 'tailwind-variants';

export const button = tv({
  base: 'btn',
  defaultVariants: {
    bordered: false,
    disabled: false,
    fullWidth: false,
    intent: 'secondary',
    size: 'md',
  },
  variants: {
    bordered: {
      true: 'border border-i-border-glass-intented-active! border-tab',
    },
    btnStyle: {
      dash: 'btn-dash',
      ghost: 'btn-ghost',
      link: 'btn-link',
      outline: 'btn-outline',
      soft: 'btn-soft',
    },
    disabled: {
      true: 'opacity-disabled-30',
    },
    fullWidth: {
      true: 'w-full',
    },
    intent: {
      accent: 'btn-accent',
      error: 'btn-error',
      info: 'btn-info',
      link: 'font-normal',
      neutral: 'btn-neutral',
      primary: 'btn-primary',
      secondary: 'btn-secondary! text-semantic-secondary-content',
      warning: 'btn-warning',
    },
    size: {
      icon: 'size-8 p-1-5',
      lg: 'btn-lg p-4',
      md: 'btn-md p-3',
      sm: 'btn-sm p-2',
      xl: 'btn-lg p-4', // we do not use Daisy UI XL -> Fallback to lg
      xs: 'btn-xs p-1-5',
    },
  },
});
export type ButtonVariants = VariantProps<typeof button>;

@Component({
  selector: 'app-button',
  imports: [CommonModule],
  templateUrl: './button.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.pointer-events]': 'isDisabled() ? "none" : "auto"',
  },
})
export class ButtonComponent {
  #router = inject(Router);

  readonly testId = input<string>();
  readonly size = input<ButtonVariants['size']>();
  readonly states = input<'default' | 'loading'>('default');
  readonly intent = input<ButtonVariants['intent']>();
  readonly btnStyle = input<ButtonVariants['btnStyle']>();
  readonly fullWidth = input<ButtonVariants['fullWidth']>();
  readonly bordered = input<ButtonVariants['bordered']>();
  readonly ariaLabel = input<string>('');
  readonly link = input<string>();
  readonly component = input<HTMLButtonElement['type']>('button');
  readonly disabled = input<boolean>(false);
  readonly forceBaseBgColor = input<boolean>(false);
  readonly hasClickEventWhenDisabled = input<boolean>(false);

  readonly handleClick = output<Event>();

  readonly isDisabled = computed(
    () =>
      (this.disabled() && !this.hasClickEventWhenDisabled()) ||
      this.states() === 'loading'
  );

  readonly classes = computed(() =>
    button({
      bordered: this.bordered(),
      btnStyle: this.btnStyle(),
      disabled: this.disabled(),
      fullWidth: this.fullWidth(),
      intent: this.intent(),
      size: this.size(),
    })
  );

  onClick(event: Event): void {
    this.handleClick.emit(event);
    if (this.link()) {
      this.#router.navigate([this.link()]);
    }
  }
}
